/**
 * product_create - Create a new Product entity for shop websites
 * 
 * Creates a Product entity with all necessary fields for e-commerce.
 * Optionally creates Stripe Price ID if Stripe integration is connected.
 */

import { 
  StandardToolResult, 
  createSuccessResult, 
  createErrorResult 
} from '../../types/standardToolResults';
import { SelfContainedCommand } from '../../types/commands';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('PRODUCT_CREATE');

interface ProductCreateArgs {
  name: string;
  price: number;  // Price in cents (e.g., 2500 = $25.00)
  currency?: string;  // Default: 'usd'
  description?: string;
  imageUrl?: string;
  inStock?: boolean;
  inventory?: number | null;  // null = unlimited
  category?: string;
  websiteId?: string;  // Website to associate with (creates entity edge)
  createStripePrice?: boolean;  // If true, create Stripe Price ID

  // Dropshipping metadata — set these when product comes from CJDropshipping or another supplier
  sourcePid?: string;         // Supplier's product ID (e.g. CJDropshipping pid)
  sourceApi?: string;         // 'cj_dropshipping' | 'zinc' | etc.
  sourceProductUrl?: string;  // Link to product on supplier site
  costPrice?: number;         // Wholesale cost in cents
  markup?: number;            // Markup percentage applied (e.g. 30 = 30%)
  supplierName?: string;      // Human-readable supplier name
  shippingTime?: string;      // Shipping estimate (e.g. "7-14 days")
  isDropshipped?: boolean;    // Marks product as dropshipped
  stripeProductId?: string;   // Stripe Product ID (if already created)
  stripePriceId?: string;     // Stripe Price ID (if already created)
  retailer?: string;          // For Zinc: 'amazon', 'walmart', etc.
}

export async function product_create(
  args: ProductCreateArgs,
  context: any
): Promise<StandardToolResult> {
  const startTime = Date.now();

  try {
    log.info('[product_create] Creating product:', args.name);

    const {
      name,
      price,
      currency = 'usd',
      description = '',
      imageUrl = '',
      inStock = true,
      inventory = null,
      category = '',
      websiteId,
      createStripePrice = false,
      sourcePid,
      sourceApi,
      sourceProductUrl,
      costPrice,
      markup,
      supplierName,
      shippingTime,
      isDropshipped,
      stripeProductId,
      stripePriceId: stripePriceIdArg,
      retailer,
    } = args;

    // Validate
    if (!name || name.trim().length === 0) {
      return createErrorResult(
        'Product name is required',
        'Product name cannot be empty',
        'validation'
      );
    }

    if (price == null || price < 0) {
      return createErrorResult(
        'Valid price is required',
        'Price must be a positive number in cents (e.g., 2500 for $25.00)',
        'validation'
      );
    }

    // Optionally create Stripe Price ID first
    const stripePriceId = stripePriceIdArg;
    
    if (createStripePrice && !stripePriceId) {
      log.warn('[product_create] Stripe Price auto-creation not yet implemented — pass stripePriceId directly');
    }

    // Create Product entity
    const { entityService } = await import('../../services/entityService');

    const skeleton: Record<string, any> = {
      '@type': 'Product',
      price: price,
      currency: currency.toLowerCase(),
      description: description,
      imageUrl: imageUrl,
      inStock: inStock,
      inventory: inventory,
      category: category,
      stripePriceId: stripePriceId,
      createdBy: 'product_create',
      createdAt: new Date().toISOString()
    };

    // Persist dropshipping metadata when provided
    if (isDropshipped !== undefined) skeleton.isDropshipped = isDropshipped;
    if (sourcePid) skeleton.sourcePid = sourcePid;
    if (sourceApi) skeleton.sourceApi = sourceApi;
    if (sourceProductUrl) skeleton.sourceProductUrl = sourceProductUrl;
    if (costPrice !== undefined) skeleton.costPrice = costPrice;
    if (markup !== undefined) skeleton.markup = markup;
    if (supplierName) skeleton.supplierName = supplierName;
    if (shippingTime) skeleton.shippingTime = shippingTime;
    if (stripeProductId) skeleton.stripeProductId = stripeProductId;
    if (retailer) skeleton.retailer = retailer;

    const product = await entityService.create({
      entityType: 'Product',
      name: name,
      icon: 'ShoppingBag',
      spaceId: context.spaceId,
      skeleton
    });

    log.info('[product_create] Product created:', product._id);

    // Link to website if websiteId provided
    if (websiteId) {
      try {
        log.info('[product_create] Linking product to website:', { productId: product._id, websiteId });
        await entityService.addLink(product._id, websiteId, 'available_on');
        log.info('[product_create] ✅ Product linked to website');
      } catch (linkError) {
        log.error('[product_create] Failed to link product to website:', linkError);
      }
    }

    const duration = Date.now() - startTime;

    const message = stripePriceId 
      ? `Created product "${name}" (${formatPrice(price, currency)}) with Stripe Price ID: ${stripePriceId}`
      : `Created product "${name}" (${formatPrice(price, currency)})`;
    
    return createSuccessResult(
      message,
      {
        productId: product._id,
        name: name,
        price: price,
        currency: currency,
        imageUrl: imageUrl || null,
        stripePriceId: stripePriceId || null,
        sourcePid: sourcePid || null,
        sourceApi: sourceApi || null,
        costPrice: costPrice || null,
        markup: markup || null,
        supplierName: supplierName || null,
        websiteId: websiteId || null,
        duration: duration
      }
    );

  } catch (error: any) {
    log.error('[product_create] Error:', error);
    return createErrorResult(
      'Failed to create product',
      error,
      'execution'
    );
  }
}

function formatPrice(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(cents / 100);
}

export const productCreateCommand: SelfContainedCommand = {
  id: 'product_create',
  description: `Create a new Product entity for a shop.

**IMPORTANT: Use REAL product data — never invent placeholder products.**

For dropshipping stores, call \`api_connect\` with CJDropshipping FIRST to get real product
data, then pass that data here. Do NOT use via.placeholder.com images or made-up descriptions.

**DROPSHIPPING WORKFLOW (CJDropshipping):**

1. Search for real products:
   \`\`\`
   api_connect({
     managedApiId: "managed_cjdropshipping",
     method: "GET",
     endpoint: "/product/listV2",
     queryParams: { keyWord: "<niche>", pageNumber: 1, pageSize: 20 }
   })
   \`\`\`

2. For each real product from the API response, call product_create with the actual data:
   \`\`\`
   product_create({
     name: product.productNameEn,
     price: Math.round(parseFloat(product.sellPrice) * 1.30 * 100), // 30% markup, in cents
     description: product.description || product.productNameEn,
     imageUrl: product.productImage,   // Real image URL from CJDropshipping
     category: product.categoryName,
     websiteId: "<entityId>",
     // Dropshipping metadata:
     isDropshipped: true,
     sourcePid: product.pid,
     sourceApi: "cj_dropshipping",
     costPrice: Math.round(parseFloat(product.sellPrice) * 100),
     markup: 30,
     supplierName: "CJDropshipping",
     shippingTime: product.shippingTime
   })
   \`\`\`

**PRICE FIELDS:**
- \`price\` = retail price in cents (what customer pays)
- \`costPrice\` = wholesale cost in cents (what you pay supplier)

**IMAGE URLS:** Must be real URLs from the supplier API. Never use placeholder.com or generate fake URLs.
`,
  
  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Product name — use the real name from the supplier API (e.g., product.productNameEn)',
        required: true
      },
      {
        name: 'price',
        type: 'number',
        description: 'Retail price in cents (e.g., 3890 = $38.90). Apply markup to costPrice.',
        required: true
      },
      {
        name: 'currency',
        type: 'string',
        description: 'Currency code (default: "usd")',
        required: false,
        defaultValue: 'usd'
      },
      {
        name: 'description',
        type: 'string',
        description: 'Product description — use real description from supplier, not invented text',
        required: false
      },
      {
        name: 'imageUrl',
        type: 'string',
        description: 'Real product image URL from the supplier API (e.g., product.productImage). Never use placeholder URLs.',
        required: false
      },
      {
        name: 'inStock',
        type: 'boolean',
        description: 'Whether product is in stock',
        required: false,
        defaultValue: true
      },
      {
        name: 'inventory',
        type: 'number',
        description: 'Inventory count (null = unlimited)',
        required: false
      },
      {
        name: 'category',
        type: 'string',
        description: 'Product category (use categoryName from supplier API)',
        required: false
      },
      {
        name: 'websiteId',
        type: 'string',
        description: 'app_project or website_project entity ID — auto-links product to the shop',
        required: false
      },
      {
        name: 'isDropshipped',
        type: 'boolean',
        description: 'Set true for dropshipped products',
        required: false
      },
      {
        name: 'sourcePid',
        type: 'string',
        description: 'Supplier product ID (e.g., CJDropshipping product.pid). Required for fulfillment.',
        required: false
      },
      {
        name: 'sourceApi',
        type: 'string',
        description: 'Supplier API identifier: "cj_dropshipping" | "zinc" | etc.',
        required: false
      },
      {
        name: 'sourceProductUrl',
        type: 'string',
        description: 'URL of the product on the supplier site',
        required: false
      },
      {
        name: 'costPrice',
        type: 'number',
        description: 'Wholesale cost in cents (e.g., product.sellPrice * 100). Used for profit tracking.',
        required: false
      },
      {
        name: 'markup',
        type: 'number',
        description: 'Markup percentage applied (e.g., 30 means 30% markup over costPrice)',
        required: false
      },
      {
        name: 'supplierName',
        type: 'string',
        description: 'Human-readable supplier name (e.g., "CJDropshipping")',
        required: false
      },
      {
        name: 'shippingTime',
        type: 'string',
        description: 'Shipping time estimate from supplier (e.g., "7-14 days")',
        required: false
      },
      {
        name: 'stripePriceId',
        type: 'string',
        description: 'Stripe Price ID (set after creating price via Stripe API)',
        required: false
      },
      {
        name: 'stripeProductId',
        type: 'string',
        description: 'Stripe Product ID (set after creating product via Stripe API)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          name: 'Western Saddle',
          price: 45000,
          currency: 'usd',
          description: 'Premium leather western saddle',
          inStock: true
        },
        expectedOutput: {
          status: 'success',
          message: 'Created product "Western Saddle" ($450.00)',
          data: {},
          format: 'json' as const
        },
        description: 'Create a product with basic info'
      }
    ],
    bad: [
      {
        input: { name: 'Test', price: -100 },
        error: 'Valid price is required (in cents)',
        why: 'Price cannot be negative',
        fix: 'Use positive price in cents (e.g., 2500 for $25.00)'
      }
    ]
  },
  
  handler: product_create
};
